import { Grid, Card, Button, Box, CardHeader } from "@mui/material";
import { FC } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { OdResourcePageProps } from "~/types/resource";
import { Content } from "../../components/content/Content";
import CreateOrEdit from "~/controller/CreateOrEdit";
import WithSave from "~/controller/WithSave";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION, MANAGE_DATA_CATALOG } from "~/constants";
import { ProtectionRuleInput } from "~/gen/api/securefile";
import { ClassificationRuleAmbinetDataCotnextProvider } from "~/components/securefiles/ClassificationRuleAmbientDataProvider";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import { DataProtectionRuleEdit, useDataProtectionRuleEditController } from "~/components/securefiles/DataProtectionRuleEdit";


const CreateDataProtectionRule: FC<OdResourcePageProps> = (props) => {
  const handlers = useDefaultSavedHandlers("Create Data Protection Rule")

  return (
    <ClassificationRuleAmbinetDataCotnextProvider>
      <CreateOrEdit resource={props.resource} basePath="" {...handlers}>
        <CreateDataProtectionRuleBody {...props} />
      </CreateOrEdit>
    </ClassificationRuleAmbinetDataCotnextProvider>
  )
}

const CreateDataProtectionRuleBody: FC<OdResourcePageProps> = ({
  options, resource,
}) => {
  const hasClssPermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const hasEditPermit = useHasPermit([MANAGE_DATA_CATALOG])
  const dispatch = useDispatch()
  
  const ctrls = useDataProtectionRuleEditController()
  const { dirty, formDataValid, 
    states,
    validateForm,
    ruleComposeCtrls: { validate: validateComposedRule, getComposedRule, states: { isDirty: ruleDefDirty } },
  } = ctrls
  
  const handleSave = (save: (record:any,callbacks?:any)=>void) => async () => {
    if (!hasClssPermit && !hasEditPermit) { return }
    if (!dirty || !formDataValid) { return }
    
    if (!(await validateForm())) { return }
    const composedRuleValid = await validateComposedRule()
    if (!composedRuleValid) { return }
    const composedRule = getComposedRule()
    if (!composedRule) { return }
    const payload: ProtectionRuleInput = {
      displayName: states.stage.displayName,
      comment: states.stage.comment,
      ruleDisplayTree: JSON.stringify(composedRule) 
    }

    save(payload, {
      onSuccess: () => {
        dispatch(push(`/${resource}`))
      }
    })
  }
  return (
    <Content 
      pageName="Create Data Protection Rules" 
      resourceName="Create" 
      breadcrumbs={[...(options?.breadcrumbs ?? []), {
        label: 'Data Protection Rules',
        path: `/${resource}`
      }]}
      mainActions={
        <>
          <WithSave 
            render={({save, saving}) =>
              <Button
                color="primary"
                disabled={!dirty || !formDataValid || saving}
                variant="contained"
                onClick={handleSave(save)}
              >
                Save
              </Button>}
          />
          <Button
            color="primary"
            variant="outlined"
            component={RouterLink}
            to={`/${resource}`}
          >
            Cancel
          </Button>
        </>
      }
    >
      <Grid container spacing={4} sx={{ mt: 2 }} >
        <Grid item xs={12}>
          <Card sx={{ minHeight: 600 }}>
            <CardHeader title="New Data Protection Rule" />
            <Box sx={{ mb: 2, px: 4, }}>
              <DataProtectionRuleEdit {...ctrls} />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Content>
  )
}

export default CreateDataProtectionRule;
